import "js/studios/shared/app_base"
import {createRoot} from "react-dom/client"
import {registerReducer} from 'js/studios/shared/redux/utils'
import React, {useState, useMemo, useContext, useCallback} from 'react'
import {Provider} from 'react-redux'
import {DeliveryDateFrame, RingRecipientFrame, CenterStoneFrame, DesignVisionFrame} from './engagement'
import {ChooseCategoryTopFrame, ChooseCategorySecondaryFrame} from './non_engagement'
import {ClassRingMetalColor, ClassRingFinish, ClassRingGemstones, ClassRingFaceShape, ClassRingRaisedOrRecessed, ClassRingShoulderDesigns, ClassRingFaceSize, ClassRingNextSteps} from './class_ring'
import {SignetMetalColor, SignetFinish, SignetGemstones, SignetFaceShape, SignetRaisedOrRecessed, SignetShoulderDesigns, SignetRingFaceSize, SignetNextSteps} from './signet'
import {PendantMetalColor, PendantFinish, PendantGemstones, SolidOrCutout, PendantShape, PendantSize, PendantNextSteps, PendantChainLength, PendantChainStyles} from './pendant'
import {ErrorBoundary} from 'js/studios/shared/error_boundary'
import {trackEventSP} from 'js/studios/shared/tracking'
import {FlowFramesCtx, FlowFramesProvider} from 'js/studios/shared/unredux/contexts/flow'
import {KeyValueCtx, KeyValueProvider} from 'js/studios/shared/unredux/contexts/key_value'
import {useOnUpdate} from 'js/studios/shared/unredux/hooks'
import {toKebabCase} from 'js/studios/shared/utils/strings'
import {BudgetFrame, LowBudgetPreventionFrame, ShippingLocationFrame, SourceFrame, ShareYourIdea} from './shared'
import {Splash} from 'js/studios/shared/flows'
import {Registration} from './registration'
import {onPageReady} from 'js/studios/shared/utils'

// legacy support - registration still depends on user in redux store.
registerReducer((state, action) => {
  switch (action.type) {
    case "USER_LOGIN":
      return action.user
    default:
      return state || window.cm.studiosUser
  }
}, 'user', true)

const CATEGORY_CTA_MAPPING = {
  signet_ring: 'a signet ring',
  class_ring: 'a class ring',
  engagement_ring: 'an engagement ring',
  necklace: 'a necklace',
  earrings: 'earrings',
  cufflinks: 'cufflinks',
  lapelpin: 'a pin',
}

function Welcome() {
  const splashImage = window.cm.bootstrap.splashImage
  const {data: {sourceCategory}, setMany} = useContext(KeyValueCtx)
  const {gotoNextFrame} = useContext(FlowFramesCtx)

  const
    goToGenericFlow = useCallback(ev => {
      ev.preventDefault()
      trackEventSP('click_switch_to_generic_flow', 'consultation', {source_category: sourceCategory})
      setMany({sourceCategory: null, flow: "generic"})
    }, [setMany, sourceCategory]),
    splashImgClass = useMemo(() => {
      if (splashImage) {
        return `splash__hero--${toKebabCase(splashImage)}`
      } else if (sourceCategory) {
        return `splash__hero--${toKebabCase(sourceCategory)}`
      }
      return null
    }, [splashImage, sourceCategory])

  return <Splash splashClassName={splashImgClass}>
    <h1 className="mb--1_5">Let’s Make Something Amazing</h1>
    <p className="mb--1_5">Before we begin, we’d like to learn a bit about what you're looking for so we can give you the best experience possible.</p>
    <p>
      <a href="#" className="btn btn--lg btn--wide btn--arrow-right" tabIndex="0" onClick={gotoNextFrame}>Let’s Go</a>
    </p>
    {sourceCategory && CATEGORY_CTA_MAPPING[sourceCategory] &&
      <a
        href="#"
        tabIndex="0"
        onClick={goToGenericFlow}>
          <small>Not creating {CATEGORY_CTA_MAPPING[sourceCategory]}?</small>
      </a>}
  </Splash>
}

function ProjectCreationInner() {
  const [preventionFrame, setPreventionFrame] = useState(window.cm.bootstrap.preventionFrame)
  const {data: {flow, categoryTop, categorySecondary, sourceCategory}} = useContext(KeyValueCtx)

  const frames = useMemo(() => {
    const rv = [<Welcome key="welcome" />]

    if (flow === 'generic') {
      rv.push(<ChooseCategoryTopFrame key="choose-category-top" />)
    }

    if (flow === 'engagement' || categoryTop === 'engagement') {
      rv.push(
        <RingRecipientFrame key="recipient" />,
        <DesignVisionFrame key="design-vision" />,
        <CenterStoneFrame key="center-stone" />,
        <BudgetFrame key="budget" setPreventionFrame={setPreventionFrame} />,
        <DeliveryDateFrame key="delivery-date" />,
      )
    } else {
      if (!sourceCategory) {
        rv.push(<ChooseCategorySecondaryFrame key="choose-category-secondary" />)
      }

      if (categorySecondary === "signet_ring" || sourceCategory === "signet_ring") {
        rv.push(
          <SignetMetalColor key="metal-color" />,
          <SignetFinish key="finish" />,
          <SignetGemstones key="gemstones" />,
          <SignetFaceShape key="ring-face-shape" />,
          <SignetRingFaceSize key="ring-face-size" />,
          <SignetRaisedOrRecessed key="raised-or-recessed" />,
          <SignetShoulderDesigns key="shoulder-designs" />,
          <ShareYourIdea key="share-your-idea" />,
          <BudgetFrame key="budget" setPreventionFrame={setPreventionFrame} />,
          <SignetNextSteps key="next-steps" />,
        )
      } else if (categorySecondary === "necklace" || sourceCategory === "necklace") {
        rv.push(
          <PendantMetalColor key="pendant-metal-color" />,
          <PendantFinish key="pendant-finish" />,
          <PendantGemstones key="pendant-gemstones" />,
          <SolidOrCutout key="solid-or-cutout" />,
          <PendantShape key="pendant-face-shape" />,
          <PendantSize key="pendant-face-size" />,
          <PendantChainLength key="pendant-chain-length" />,
          <PendantChainStyles key="pendant-chain-styles" />,
          <ShareYourIdea key="share-your-idea" />,
          <BudgetFrame key="budget" setPreventionFrame={setPreventionFrame} />,
          <PendantNextSteps key="pendant-next-steps" />,
        )
      } else if (categorySecondary === "class_ring" || sourceCategory === "class_ring") {
        rv.push(
          <ClassRingMetalColor key="metal-color" />,
          <ClassRingFinish key="finish" />,
          <ClassRingGemstones key="gemstones" />,
          <ClassRingFaceShape key="ring-face-shape" />,
          <ClassRingFaceSize key="ring-face-size" />,
          <ClassRingRaisedOrRecessed key="raised-or-recessed" />,
          <ClassRingShoulderDesigns key="shoulder-designs" />,
          <ShareYourIdea key="share-your-idea" />,
          <BudgetFrame key="budget" setPreventionFrame={setPreventionFrame} />,
          <ClassRingNextSteps key="next-steps" />,
        )
      } else {
        rv.push(
          <ShareYourIdea key="share-your-idea" />,
          <BudgetFrame key="budget" setPreventionFrame={setPreventionFrame} />,
        )
      }
    }

    rv.push(
      <ShippingLocationFrame key="shipping-location" />,
      <SourceFrame key="source" />,
      <Registration key="registration" />,
    )

    return rv
  }, [flow, categoryTop, categorySecondary, sourceCategory])
  const [forcedFrame, setForcedFrame] = useState(null)

  const onFrameChange = useCallback(({prevIdx, curIdx, curFrame}) => {
    const action = prevIdx > curIdx ? 'action_previous_creation_frame' : 'action_next_creation_frame'
    trackEventSP(action, 'consultation', {frame: curFrame.key})
    window.scrollTo({top: 0})
  }, [])

  useOnUpdate((prevSourceCategory, _prevFlow) => {
    // move to the choose top category frame when user switches to generic flow
    if (prevSourceCategory && !sourceCategory && flow === "generic") {
      setForcedFrame("choose-category-top")
    } else {
      setForcedFrame(null)
    }
  }, [sourceCategory, flow])

  if (preventionFrame === 'prevention-low-budget') {
    return <LowBudgetPreventionFrame />
  } else {
    return <FlowFramesProvider frames={frames} curFrame={forcedFrame} onChange={onFrameChange} />
  }
}

const DEFAULT_PROJECT_CREATION_DATA = {
  flow: "engagement",
  sourceCategory: null,
  category: null,
  budgetMin: "",
  budgetMax: "",
  budgetSkip: false,
  details: "",
  recipient: "",
  intent: "",
  significantOtherName: "",
  significantOtherNameSkip: false,
  centerStoneTypes: [],
  centerStoneSkip: false,
  designVisionSources: [],
  designVisionSkip: false,
  timeline: "",
  deliveryDate: null,
  categoryTop: "",
  categorySecondary: "",
  source: "",
  attachments: [],
  hidden_useragent: window.navigator.userAgent,
}

function ProjectCreationApp() {
  return <KeyValueProvider defaults={DEFAULT_PROJECT_CREATION_DATA} data={{...DEFAULT_PROJECT_CREATION_DATA, ...window.cm.initialState.projectCreationData}}>
    <ProjectCreationInner />
  </KeyValueProvider>
}

onPageReady(() => {
  createRoot(document.getElementById('project-creation-app')).render(
    // keeping redux store for state.user still used by some shared registration components
    <Provider store={window.cm.store}>
      <ErrorBoundary><ProjectCreationApp /></ErrorBoundary>
    </Provider>
  )
})
